import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { slugify, BLOG_ANSWER_KEYS, type Translatable } from '@hrlabs/shared-types';
import { getRowBy, saveRow } from '../../lib/db';
import { uploadToStorage } from '../../lib/upload';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../lib/auth';
import { BilingualField } from '../../components/BilingualField';
import { translateBlogPost } from '../../lib/functions';

const EMPTY: Translatable = { en: '', bg: '' };
type Answers = Record<(typeof BLOG_ANSWER_KEYS)[number], Translatable>;
const emptyAnswers = (): Answers =>
  Object.fromEntries(BLOG_ANSWER_KEYS.map((k) => [k, { ...EMPTY }])) as Answers;

export function BlogEdit() {
  const { slug } = useParams();
  const isNew = !slug || slug === 'new';
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user } = useAuth();

  const [docId, setDocId] = useState<string | undefined>();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [openAnswers, setOpenAnswers] = useState(false);
  const [thumbFile, setThumbFile] = useState<File | null>(null);

  const [title, setTitle] = useState<Translatable>({ ...EMPTY });
  const [subtitle, setSubtitle] = useState<Translatable>({ ...EMPTY });
  const [description, setDescription] = useState<Translatable>({ ...EMPTY });
  const [answers, setAnswers] = useState<Answers>(emptyAnswers());
  const [status, setStatus] = useState('draft');
  const [thumbnailPath, setThumbnailPath] = useState<string | null>(null);
  const [authorId, setAuthorId] = useState('');
  const [authorName, setAuthorName] = useState('');

  useEffect(() => {
    if (isNew) return;
    getRowBy('blogPosts', 'slug', slug!).then((r) => {
      if (r) {
        setDocId(r.id);
        setTitle((r.title as Translatable) ?? { ...EMPTY });
        setSubtitle((r.subtitle as Translatable) ?? { ...EMPTY });
        setDescription((r.description as Translatable) ?? { ...EMPTY });
        const a = emptyAnswers();
        for (const k of BLOG_ANSWER_KEYS) a[k] = (r[k] as Translatable) ?? { ...EMPTY };
        setAnswers(a);
        setStatus(String(r.status ?? 'draft'));
        setThumbnailPath((r.thumbnailPath as string | null) ?? null);
        setAuthorId(String(r.authorId ?? ''));
        setAuthorName(String(r.authorName ?? ''));
      }
      setLoading(false);
    });
  }, [isNew, slug]);

  const computedSlug = useMemo(() => slugify(title.en || title.bg || ''), [title]);

  const runTranslate = async () => {
    setTranslating(true);
    try {
      const payload: Record<string, string> = { description_bg: description.bg };
      for (const k of BLOG_ANSWER_KEYS) if (answers[k].bg.trim()) payload[`${k}_bg`] = answers[k].bg;
      const res = await translateBlogPost(payload as never);
      const data = res.data.data as Record<string, string>;
      if (data.description) setDescription((d) => ({ ...d, en: data.description }));
      setAnswers((prev) => {
        const next = { ...prev };
        for (const k of BLOG_ANSWER_KEYS) if (data[k]) next[k] = { ...next[k], en: data[k]! };
        return next;
      });
      notify('success', 'Translated the English fields.');
    } catch (err) {
      notify('danger', `Translate failed: ${(err as Error).message}`);
    } finally {
      setTranslating(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.bg.trim() || !description.bg.trim()) {
      notify('danger', 'Bulgarian title and description are required.');
      return;
    }
    setSaving(true);
    try {
      const newSlug = computedSlug || docId || `post-${Date.now()}`;
      const id = await saveRow('blogPosts', docId, {
        title,
        subtitle,
        description,
        slug: newSlug,
        status,
        authorId: authorId || user?.uid || '',
        authorName: authorName || user?.displayName || user?.email || 'HRLabs',
        ...answers,
      });
      if (thumbFile) {
        const { path } = await uploadToStorage('blog-thumbnails', id, thumbFile);
        await saveRow('blogPosts', id, { thumbnailPath: path });
        setThumbnailPath(path);
      }
      notify('success', 'Post saved.');
      navigate(`/blog/${newSlug}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="fw-bold m-0">{isNew ? 'New post' : 'Edit post'}</h3>
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          onClick={runTranslate}
          disabled={translating || !description.bg.trim()}
        >
          {translating ? 'Translating…' : 'Translate with AI'}
        </button>
      </div>

      <BilingualField label="Title" value={title} onChange={setTitle} required rows={1} />
      <BilingualField label="Subtitle" value={subtitle} onChange={setSubtitle} rows={1} />
      <BilingualField label="Description" value={description} onChange={setDescription} required rich />

      <div className="mb-3">
        <button
          type="button"
          className="btn btn-sm btn-light border"
          onClick={() => setOpenAnswers((o) => !o)}
        >
          {openAnswers ? '▾' : '▸'} Interview answers (1–13)
        </button>
        {openAnswers && (
          <div className="mt-3 ps-2 border-start">
            {BLOG_ANSWER_KEYS.map((k, i) => (
              <BilingualField
                key={k}
                label={`Answer ${i + 1}`}
                value={answers[k]}
                onChange={(v) => setAnswers((a) => ({ ...a, [k]: v }))}
                rich
              />
            ))}
          </div>
        )}
      </div>

      <div className="mb-3" style={{ maxWidth: 260 }}>
        <label className="form-label fw-semibold">Status</label>
        <select
          className="form-select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="draft">draft</option>
          <option value="published">published</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Thumbnail</label>
        <input
          type="file"
          className="form-control"
          accept="image/*"
          onChange={(e) => setThumbFile(e.target.files?.[0] ?? null)}
        />
        {thumbnailPath && <div className="small text-muted mt-1">current: {thumbnailPath}</div>}
      </div>

      <p className="small text-muted">slug: {computedSlug || '(from English title)'}</p>

      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
